import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { PMP_SUPPORT_URL } from "../../constants/pMpConstants";
import { useAuth } from "../../hooks/useAuth";
import { useIntercomCustom } from "../../hooks/useIntercomCustom";
import { mixpanelActions } from "../../utils/mixpanel";
import {
  ContactUsButton,
  Content,
  Heading,
  Illustration,
  Message,
  TextColumn,
  TryAgainButton,
} from "./NotFound.styles";

/**
 * Why this page is on screen. The user sees the same thing either way — the
 * ticket asks for the 404 page on a failed request too, and there is one
 * design — but the analytics must tell them apart, or a render crash gets
 * triaged as a missing route.
 */
export type NotFoundReason = "not-found" | "error";

const PAGE_VIEW_EVENT: Record<NotFoundReason, string> = {
  // EN-2750 specifies this name literally. Note it is neither of the portal's
  // existing page-view prefixes ("Visited Screen:" / "PageView:").
  "not-found": "Page viewed: 404",
  // Follows the repo's existing "Error view: X" convention (utils/blurDetection.ts).
  error: "Error view: Render crash",
};

interface INotFoundContentProps {
  reason?: NotFoundReason;
}

/**
 * The 404 block itself, without any chrome — `NotFound` wraps it in the
 * pre-login bar, the catch-all route wraps it in the real portal top panel,
 * and the ErrorBoundaries render it when a page throws mid-render.
 */
export function NotFoundContent({
  reason = "not-found",
}: INotFoundContentProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { setIsIntercomOpen } = useIntercomCustom();

  useEffect(() => {
    document.title = t("notFound_documentTitle");

    mixpanelActions.track(PAGE_VIEW_EVENT[reason]);
  }, [reason, t]);

  const handleContactUsClick = () => {
    mixpanelActions.track("UserAction: SupportButton");

    // Intercom is only booted for a signed-in user (see IntercomContext), so
    // without a session the only route to support is the public page.
    if (isAuthenticated) {
      setIsIntercomOpen(true);
      return;
    }

    // `noopener` is not implied by window.open the way it is for an anchor
    // with target="_blank" — without it the new tab keeps a window.opener
    // handle back to the portal.
    window.open(PMP_SUPPORT_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <Content>
      <TextColumn>
        <Heading>{t("notFound_heading")}</Heading>

        <Message>
          <Trans
            i18nKey="notFound_message"
            components={{
              contact: (
                <ContactUsButton type="button" onClick={handleContactUsClick} />
              ),
            }}
          />
        </Message>

        <TryAgainButton
          type="button"
          label={t("notFound_tryAgain")}
          // A full reload is what the ticket asks for, and it doubles as the
          // reset when this is rendered as an ErrorBoundary fallback.
          onClick={() => window.location.reload()}
        />
      </TextColumn>

      <Illustration aria-hidden focusable="false" />
    </Content>
  );
}
